import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { LogOut, Users, Megaphone, Download, GraduationCap, ArrowUpRight, ArrowLeft, Share2, Clapperboard, Settings, Gamepad2, Bell, Briefcase, FolderKanban, Trophy, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { renderInline } from "./inline";
import LearnSection from "./LearnSection";
import ReelsFeed from "./ReelsFeed";
import GamesSection from "./GamesSection";
import MyServices from "./MyServices";
import MyProjects from "./MyProjects";
import "../portfolio/styles.css";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const API = process.env.REACT_APP_BACKEND_URL;

/* ---------- in-app browser detection ---------- */
function isInAppBrowser() {
  const ua = navigator.userAgent || "";
  return /FBAN|FBAV|Instagram|Twitter|LinkedIn|MicroMessenger|Line\/|Snapchat|Pinterest|TikTok/i.test(ua)
    || (/iPhone|iPad|iPod/.test(ua) && /AppleWebKit/.test(ua) && !/Safari/.test(ua))
    || (/Android/.test(ua) && /wv/.test(ua));
}

/* ---------- Google OAuth callback: exchanges ?code & ?state then lands back ---------- */
export function GoogleCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [cbError, setCbError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const errorParam = params.get("error");
    const dest = sessionStorage.getItem("post_auth_dest") || "/tribe";
    sessionStorage.removeItem("post_auth_dest");
    if (errorParam) {
      setCbError(`Google returned: ${errorParam}`);
      setTimeout(() => navigate(dest, { replace: true }), 3000);
      return;
    }
    if (!code || !state) {
      setCbError("Missing authorization code — please try signing in again.");
      setTimeout(() => navigate(dest, { replace: true }), 3000);
      return;
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    fetch(`${API}/api/auth/google/callback`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ code, state, redirect_uri: window.location.origin + "/auth/google" }),
    })
      .then(async (r) => {
        if (r.ok) return r.json();
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `Server error (${r.status})`);
      })
      .then((d) => navigate(dest, { replace: true, state: { user: d.user } }))
      .catch((err) => {
        setCbError(err.message || "Sign-in didn't complete — please try again.");
        setTimeout(() => navigate(dest, { replace: true }), 4000);
      });
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "#EDE7D9" }}>
      {cbError ? (
        <div className="text-center max-w-sm">
          <p className="rp-display text-lg text-red-700 mb-2">Sign-in issue</p>
          <p className="text-sm text-[#5C5042]">{cbError}</p>
          <p className="text-xs text-[#8A7E6B] mt-3">Redirecting back…</p>
        </div>
      ) : (
        <p className="rp-display rp-italic text-lg text-[#5C5042]">Signing you in…</p>
      )}
    </div>
  );
}

/* ---------- login gate: email/password + white-label Google ---------- */
const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ") || "Invalid input.";
  return String(detail);
};

const gateInput = "tribal-input";

function LoginGate({ message, onAuthed }) {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inApp = useState(() => isInAppBrowser())[0];
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openInBrowser = () => {
    const url = window.location.href;
    if (/android/i.test(navigator.userAgent)) {
      window.location.href = `intent://${url.replace(/^https?:\/\//, "")}#Intent;scheme=https;end`;
      return;
    }
    window.open(url, "_blank");
  };

  const googleLogin = async () => {
    if (inApp) { openInBrowser(); return; }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    sessionStorage.setItem("post_auth_dest", window.location.pathname);
    try {
      const r = await fetch(`${API}/api/auth/google/url`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ redirect_uri: window.location.origin + "/auth/google" }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `Request failed (${r.status})`);
      }
      const d = await r.json();
      window.location.href = d.url;
    } catch (err) {
      setError(err.message || "Google sign-in is unavailable right now. Please try email login.");
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    if (mode === "signup" && !form.name?.trim()) return setError("Please enter your name.");
    if (!form.email?.trim() || !form.password) return setError("Email and password are required.");
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/auth/${mode === "login" ? "login" : "register"}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(mode === "login"
          ? { email: form.email, password: form.password }
          : { name: form.name, email: form.email, password: form.password }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(formatApiErrorDetail(d.detail));
      onAuthed(d.user);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-10 tribal-bg tribal-bg-pattern relative overflow-hidden" data-testid="tribe-login-gate">
      {/* Warli geometric corner accents */}
      <div className="absolute top-6 right-6 tribal-sun w-12 h-12 md:w-16 md:h-16 opacity-60" style={{ backgroundSize: "contain" }} />
      <div className="absolute bottom-6 left-6 tribal-sun w-10 h-10 md:w-14 md:h-14 opacity-40" style={{ backgroundSize: "contain", transform: "rotate(45deg)" }} />
      {/* Top dancing figures */}
      <div className="absolute top-0 left-0 right-0 tribal-dancers" />
      {/* Bottom triangle border */}
      <div className="absolute bottom-0 left-0 right-0 tribal-triangles" />
      {/* Warli art illustrations — very faint background accent */}
      <img src="/warli-art/dancers.jpg" alt="" className="absolute bottom-0 left-0 w-56 md:w-80 opacity-[0.05] pointer-events-none select-none" style={{ filter: "brightness(1.8)" }} />
      <img src="/warli-art/tree-of-life.jpg" alt="" className="absolute top-0 right-0 w-44 md:w-64 opacity-[0.04] pointer-events-none select-none" style={{ filter: "brightness(1.8)" }} />

      <div className="tribal-card max-w-md w-full p-8 md:p-10 relative z-10" style={{ borderRadius: "12px" }}>
        <div className="text-center">
          <Users className="mx-auto mb-4 text-[#B5793F]" size={26} strokeWidth={1.5} />
          <h1 className="tribal-title text-4xl font-normal mb-2">Rosh Tribe <span className="tribal-beta-badge">Beta</span></h1>
          <p className="text-sm tribal-text leading-relaxed mb-2" style={{ textWrap: "pretty" }} data-testid="tribe-login-message">
            {message || "A private community of designers, builders and the AI-curious."}
          </p>
          <div className="tribal-divider my-6" />
        </div>

        <button onClick={googleLogin} data-testid="tribe-google-login-btn"
                className="w-full inline-flex items-center justify-center gap-3 px-6 py-3 rounded-full text-sm font-semibold text-[#F1E7D1] bg-[#22180E] border border-[#B5793F44] transition-all hover:-translate-y-0.5 hover:border-[#B5793F88]">
          <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 5.04c1.62 0 3.06.56 4.2 1.64l3.12-3.12C17.46 1.8 14.96.8 12 .8 7.48.8 3.6 3.48 1.76 7.32l3.66 2.84C6.32 7.32 8.92 5.04 12 5.04z"/><path fill="#4285F4" d="M23.2 12.26c0-.8-.08-1.58-.2-2.32H12v4.52h6.3c-.28 1.44-1.1 2.66-2.32 3.48l3.58 2.78c2.1-1.94 3.64-4.8 3.64-8.46z"/><path fill="#FBBC05" d="M5.42 14.1a7.14 7.14 0 0 1 0-4.2L1.76 7.06a11.15 11.15 0 0 0 0 10.08l3.66-3.04z"/><path fill="#34A853" d="M12 23.2c3 0 5.52-1 7.36-2.7l-3.58-2.78c-1 .68-2.28 1.08-3.78 1.08-3.08 0-5.68-2.08-6.58-4.9l-3.66 2.84C3.6 20.72 7.48 23.2 12 23.2z"/></svg>
          {inApp ? "Open in browser to sign in" : "Continue with Google"}
        </button>

        {inApp && (
          <p className="text-xs text-center tribal-muted mt-2" data-testid="in-app-browser-hint">
            Google sign-in doesn't work in app browsers. Tap above to open in {/iPhone|iPad/i.test(navigator.userAgent) ? "Safari" : "Chrome"}.
          </p>
        )}

        <div className="flex items-center gap-3 my-6">
          <span className="flex-1 h-px bg-[#B5793F30]" />
          <span className="text-[11px] uppercase tracking-[0.16em] tribal-muted">or with email</span>
          <span className="flex-1 h-px bg-[#B5793F30]" />
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "signup" && (
            <input value={form.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Your name"
                   autoComplete="name" data-testid="auth-name-input" className={gateInput} />
          )}
          <input value={form.email || ""} onChange={(e) => set("email", e.target.value)} placeholder="Email"
                 type="email" autoComplete="email" data-testid="auth-email-input" className={gateInput} />
          <input value={form.password || ""} onChange={(e) => set("password", e.target.value)}
                 placeholder={mode === "signup" ? "Password (min. 8 characters)" : "Password"}
                 type="password" autoComplete={mode === "signup" ? "new-password" : "current-password"}
                 data-testid="auth-password-input" className={gateInput} />
          {error && <p className="text-sm text-red-400" data-testid="auth-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="auth-submit-btn"
                  className="tribal-btn-primary w-full">
            {busy ? "One moment…" : mode === "login" ? "Log in" : "Create account"}
          </button>
        </form>

        <p className="text-center text-sm tribal-text mt-5">
          {mode === "login" ? "New to the Tribe?" : "Already a member?"}{" "}
          <button onClick={() => { setMode(mode === "login" ? "signup" : "login"); setError(""); }}
                  data-testid="auth-toggle-mode" className="font-semibold text-[#B5793F] underline underline-offset-2">
            {mode === "login" ? "Create an account" : "Log in"}
          </button>
        </p>
      </div>
    </div>
  );
}

/* ---------- sections ---------- */
const fmtDate = (d) =>
  new Date(d + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

function EmptyState({ text }) {
  return (
    <div className="tribal-card p-10 text-center" data-testid="tribe-empty-state">
      <p className="tribal-heading rp-italic text-base md:text-lg text-[#8A7E6B]">{text}</p>
    </div>
  );
}

export const parseBody = (raw) => {
  const blocks = [];
  for (const chunk of (raw || "").split(/\n\s*\n/)) {
    const t = chunk.trim();
    if (!t) continue;
    if (t.startsWith("## ")) blocks.push({ h: t.slice(3).trim() });
    else if (t.split("\n").every((l) => l.trim().startsWith("- ")))
      blocks.push({ ul: t.split("\n").map((l) => l.trim().slice(2)) });
    else blocks.push({ p: t });
  }
  return blocks;
};

function sharePost(slug, title) {
  const url = `${window.location.origin}/tribe/post/${slug}`;
  const done = () => toast.success(`Link to "${title}" copied`);
  if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url).then(done).catch(done);
  else done();
}

function UpdatesSection({ updates, downloads, categories }) {
  const navigate = useNavigate();
  const [cat, setCat] = useState("all");
  const chips = [{ id: "all", label: "All" }, { id: "downloads", label: "Downloads" },
    ...(categories || []).map((c) => ({ id: c.name, label: c.name }))];
  const filtered = cat === "all" ? updates : updates.filter((u) => u.category === cat);
  return (
    <div data-testid="tribe-updates-section">
      <div className="flex flex-wrap gap-2 mb-8" data-testid="updates-category-chips">
        {chips.map((c) => (
          <button key={c.id} onClick={() => setCat(c.id)} data-testid={`updates-chip-${c.id}`}
                  className="px-4 py-2 rounded-full text-[13px] font-semibold transition-all"
                  style={{
                    background: cat === c.id ? "#8D6E63" : "rgba(42,29,18,0.6)",
                    color: cat === c.id ? "#F1E7D1" : "#A69888",
                    border: "1px solid " + (cat === c.id ? "#B5793F" : "rgba(181,121,63,0.2)"),
                  }}>
            {c.label}
          </button>
        ))}
      </div>
      {cat === "downloads" ? (
        <DownloadsSection downloads={downloads} />
      ) : !filtered.length ? (
        <EmptyState text={cat === "all" ? "No updates yet — announcements will land here first." : `Nothing under "${cat}" yet — check back soon.`} />
      ) : (
        <div data-testid="tribe-updates-list">
          {filtered.map((u) => (
        <article key={u.slug} className="tribal-card p-7 md:p-8 mb-6 md:mb-8 cursor-pointer"
                 onClick={() => navigate(`/tribe/post/${u.slug}`)} data-testid={`tribe-post-card-${u.slug}`}>
          <div className="flex items-start gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#C6A97A] font-medium mb-2">
                {fmtDate(u.date)}{u.category ? ` · ${u.category}` : ""}
              </p>
              <p className="tribal-heading text-xl md:text-2xl font-medium mb-2">{u.title}</p>
              <p className="text-sm md:text-[15px] leading-relaxed tribal-text" style={{ textWrap: "pretty" }}>{u.excerpt}</p>
              <span className="inline-flex items-center gap-1.5 mt-4 text-[13px] font-medium text-[#B5793F]">
                Read post <ArrowUpRight size={14} />
              </span>
            </div>
            <button onClick={(e) => { e.stopPropagation(); sharePost(u.slug, u.title); }}
                    data-testid={`tribe-share-${u.slug}`} title="Share this post" aria-label={`Share ${u.title}`}
                    className="shrink-0 w-9 h-9 rounded-full border border-[#B5793F30] flex items-center justify-center text-[#8A7E6B] hover:text-[#F1E7D1] hover:border-[#B5793F] transition-colors">
              <Share2 size={14} />
            </button>
          </div>
        </article>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- full post view ---------- */
function PostBlock({ block }) {
  if (block.h) return <h2 className="tribal-heading text-lg md:text-xl font-medium mt-8 mb-3">{renderInline(block.h)}</h2>;
  if (block.ul)
    return (
      <ul className="space-y-2 my-4">
        {block.ul.map((li, i) => (
          <li key={i} className="flex gap-3 text-sm md:text-[15px] leading-relaxed tribal-text">
            <span className="shrink-0 mt-[9px] w-1.5 h-1.5 rounded-full" style={{ background: "#B5793F" }} />
            <span style={{ textWrap: "pretty" }}>{renderInline(li)}</span>
          </li>
        ))}
      </ul>
    );
  return <p className="text-sm md:text-[15px] leading-relaxed tribal-text my-4" style={{ textWrap: "pretty" }}>{renderInline(block.p)}</p>;
}

function PostDetail({ post }) {
  const navigate = useNavigate();
  return (
    <div data-testid={`tribe-post-${post.slug}`}>
      <div className="flex items-center justify-between mb-8">
        <button onClick={() => navigate("/tribe")} data-testid="tribe-post-back-btn"
                className="inline-flex items-center gap-2 text-sm font-medium tribal-text hover:text-[#F1E7D1] transition-colors">
          <ArrowLeft size={15} /> All updates
        </button>
        <button onClick={() => sharePost(post.slug, post.title)} data-testid="tribe-post-share-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-medium border border-[#B5793F30] tribal-text hover:text-[#F1E7D1] hover:border-[#B5793F] transition-colors">
          <Share2 size={13} /> Share
        </button>
      </div>
      <article className="tribal-card p-7 md:p-12">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#C6A97A] font-medium mb-3">{fmtDate(post.date)}</p>
        <h1 className="tribal-heading text-3xl md:text-4xl font-light mb-6" style={{ textWrap: "balance" }}>{post.title}</h1>
        {parseBody(post.body).map((b, i) => <PostBlock key={i} block={b} />)}
      </article>
    </div>
  );
}

function DownloadsSection({ downloads }) {
  if (!downloads.length) return <EmptyState text="Resources, templates and goodies will appear here soon." />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="tribe-downloads-list">
      {downloads.map((d, i) => (
        <a key={i} href={d.href} target="_blank" rel="noreferrer noopener"
           className="tribal-card p-7 flex items-start gap-4">
          <Download className="text-[#B5793F] shrink-0 mt-1" size={18} strokeWidth={1.6} />
          <span>
            <span className="tribal-heading block text-lg md:text-xl font-medium">{d.title}</span>
            {d.description && <span className="block text-sm tribal-text mt-1">{d.description}</span>}
            {d.size && <span className="block text-[11px] uppercase tracking-[0.16em] tribal-muted mt-2">{d.size}</span>}
          </span>
        </a>
      ))}
    </div>
  );
}

function NotificationBell() {
  const navigate = useNavigate();
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/tribe/notifications`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => {});
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && data.unread > 0) {
      fetch(`${API}/api/tribe/notifications/seen`, { method: "POST", credentials: "include" }).catch(() => {});
      setData((d) => ({ ...d, unread: 0 }));
    }
  };

  return (
    <div className="relative">
      <button onClick={toggle} data-testid="tribe-notif-btn" title="Notifications" aria-label="Notifications"
              className="relative w-9 h-9 rounded-full border border-[#B5793F30] flex items-center justify-center text-[#8A7E6B] hover:text-[#F1E7D1] hover:border-[#B5793F] transition-colors">
        <Bell size={14} />
        {data.unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#B5793F] text-white text-[9px] font-bold flex items-center justify-center"
                data-testid="tribe-notif-badge">{data.unread}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-11 w-80 max-w-[85vw] z-50 tribal-card shadow-2xl p-4 max-h-96 overflow-y-auto"
             data-testid="tribe-notif-panel">
          <p className="text-[11px] uppercase tracking-[0.16em] text-[#C6A97A] font-medium mb-3 px-1">What's new</p>
          {data.notifications.length === 0 && (
            <p className="text-sm tribal-muted italic px-1 pb-2">No notifications yet — daily summaries of new posts will appear here.</p>
          )}
          <div className="space-y-4">
            {data.notifications.map((n) => (
              <div key={n.id} className="px-1" data-testid={`tribe-notif-${n.id}`}>
                <p className="text-[13px] font-semibold text-[#F1E7D1]">{n.title}</p>
                <p className="text-[11px] tribal-muted mb-1.5">{n.date_key}</p>
                <div className="space-y-1">
                  {(n.items || []).map((it, i) => (
                    <button key={i} onClick={() => { setOpen(false); navigate(`/tribe/post/${it.slug}`); }}
                            className="block w-full text-left text-[13px] text-[#B5793F] hover:underline truncate">
                      • {it.title}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const TRIBE_TABS = [
  { id: "reels", label: "Reels", icon: Clapperboard },
  { id: "updates", label: "Updates", icon: Megaphone },
  { id: "learn", label: "Learn", icon: GraduationCap },
  { id: "games", label: "Games", icon: Gamepad2 },
  { id: "contests", label: "Contests", icon: Trophy },
  { id: "events", label: "Events", icon: CalendarDays },
];

function ComingSoon({ title, testId }) {
  return (
    <div className="tribal-card p-12 text-center" data-testid={testId}>
      <div className="tribal-sun mx-auto mb-4 w-10 h-10" style={{ backgroundSize: "contain", opacity: 0.5 }} />
      <p className="tribal-title text-3xl">{title}</p>
      <p className="tribal-heading rp-italic text-lg mt-3 text-[#B5793F]">Stay tuned.</p>
      <div className="tribal-divider mt-6 max-w-xs mx-auto" />
    </div>
  );
}

/* ---------- the community page ---------- */
function TribeHome({ user, onLogout, post }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("reels");
  const [content, setContent] = useState(null);
  const [myRequests, setMyRequests] = useState([]);
  const [myProjects, setMyProjects] = useState([]);

  const loadMine = () =>
    fetch(`${API}/api/services/mine`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setMyRequests)
      .catch(() => {});

  useEffect(() => {
    fetch(`${API}/api/tribe/content`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setContent)
      .catch(() => setContent({ reels: [], updates: [], downloads: [], programs: [], categories: [] }));
    loadMine();
    fetch(`${API}/api/projects/mine`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setMyProjects)
      .catch(() => {});
  }, []);

  const tabs = [
    ...TRIBE_TABS,
    ...(myRequests.length ? [{ id: "services", label: "Services", icon: Briefcase }] : []),
    ...(myProjects.length ? [{ id: "projects", label: "Projects", icon: FolderKanban }] : []),
  ];

  const c = content || { reels: [], updates: [], downloads: [], programs: [], categories: [] };
  const activePost = post ? c.updates.find((u) => u.slug === post) : null;
  return (
    <div className="min-h-screen tribal-bg tribal-bg-pattern relative" data-testid="tribe-page">
      {/* Warli decorative borders */}
      <div className="fixed top-0 left-0 right-0 tribal-dancers z-0 opacity-40" />
      <div className="fixed bottom-0 left-0 right-0 tribal-triangles z-0 opacity-30" />
      {/* Corner sun motifs */}
      <div className="fixed top-8 right-8 tribal-sun w-12 h-12 lg:w-16 lg:h-16 z-0 opacity-40" style={{ backgroundSize: "contain" }} />
      <div className="fixed bottom-8 left-8 tribal-sun w-10 h-10 lg:w-14 lg:h-14 z-0 opacity-30" style={{ backgroundSize: "contain", transform: "rotate(45deg)" }} />
      {/* Warli art illustrations — very faint background */}
      <img src="/warli-art/village-scene.jpg" alt="" className="fixed bottom-0 right-0 w-72 lg:w-[400px] opacity-[0.03] pointer-events-none select-none z-0" style={{ filter: "brightness(1.8)" }} />
      <img src="/warli-art/peacock.jpg" alt="" className="fixed top-20 left-0 w-48 lg:w-64 opacity-[0.03] pointer-events-none select-none z-0" style={{ filter: "brightness(1.8)" }} />

      <header className="relative z-10 max-w-[1600px] mx-auto px-6 lg:px-12 pt-10 pb-2 flex items-center justify-end">
        <div className="flex items-center gap-3">
          <NotificationBell />
          {user.is_admin && (
            <button onClick={() => navigate("/tribe/admin")} data-testid="tribe-admin-btn" title="Manage content"
                    className="w-9 h-9 rounded-full border border-[#B5793F30] flex items-center justify-center text-[#8A7E6B] hover:text-[#F1E7D1] hover:border-[#B5793F] transition-colors">
              <Settings size={14} />
            </button>
          )}
          {user.picture && <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full border border-[#B5793F44]" data-testid="tribe-user-avatar" />}
          <span className="text-sm tribal-text hidden sm:block" data-testid="tribe-user-name">{user.name}</span>
          <button onClick={onLogout} data-testid="tribe-logout-btn" title="Log out"
                  className="w-9 h-9 rounded-full border border-[#B5793F30] flex items-center justify-center text-[#8A7E6B] hover:text-[#F1E7D1] hover:border-[#B5793F] transition-colors">
            <LogOut size={14} />
          </button>
        </div>
      </header>

      <main className="relative z-10 max-w-[1600px] mx-auto px-6 lg:px-12 py-12 md:py-16">
        {post && content && activePost ? (
          <PostDetail post={activePost} />
        ) : post && !content ? (
          <div className="space-y-4">
            <div className="h-8 w-64 bg-[#B5793F18] rounded-xl animate-pulse" />
            <div className="h-4 w-96 bg-[#B5793F10] rounded-full animate-pulse" />
            <div className="h-64 tribal-surface rounded-lg animate-pulse mt-6" />
          </div>
        ) : (
          <>
            <h1 className="tribal-title text-4xl sm:text-5xl lg:text-6xl font-normal mb-4" style={{ textWrap: "balance" }} data-testid="tribe-welcome-title">
              Rosh Tribe <span className="tribal-beta-badge">Beta</span>
            </h1>
        <p className="tribal-heading rp-italic text-base md:text-lg text-[#A69888] max-w-2xl mb-6" style={{ textWrap: "pretty" }}>
          A private space for the community — learning, building and growing together.
        </p>

        <div className="tribal-dancers mb-4" />
        <div className="tribal-divider mb-10" />

        <nav className="flex gap-3 mb-10 w-full overflow-x-auto pb-2 scrollbar-hide" data-testid="tribe-tabs" style={{ WebkitOverflowScrolling: "touch" }}>
          {tabs.map(({ id, label, icon: Icon }) => {
            const isActive = id === tab;
            return (
              <button key={id} onClick={() => setTab(id)} data-testid={`tribe-tab-${id}`}
                      className="shrink-0 px-5 py-3 md:px-6 md:py-4 rounded-full flex items-center gap-2.5 transition-all hover:-translate-y-0.5"
                      style={{
                        background: isActive ? "#8D6E63" : "rgba(42,29,18,0.6)",
                        color: isActive ? "#F1E7D1" : "#A69888",
                        border: "1px solid " + (isActive ? "#B5793F" : "rgba(181,121,63,0.2)"),
                        boxShadow: isActive ? "0 10px 26px rgba(0,0,0,0.4), 0 0 10px rgba(181,121,63,0.15)" : "none",
                      }}>
                <Icon size={18} strokeWidth={1.8} style={{ color: "#B5793F" }} />
                <span className="text-sm md:text-base font-semibold uppercase tracking-[0.06em] whitespace-nowrap" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{label}</span>
              </button>
            );
          })}
        </nav>

        <section data-testid={`tribe-section-${tab}`}>
          {!content ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="tribal-surface rounded-lg animate-pulse" style={{ height: tab === "reels" ? "340px" : "200px" }} />
              ))}
            </div>
          ) : (
            <>
              {tab === "reels" && <ReelsFeed reels={c.reels} />}
              {tab === "updates" && <UpdatesSection updates={c.updates} downloads={c.downloads} categories={c.categories} />}
              {tab === "learn" && <LearnSection onNavigateTab={setTab} tribeContent={c} />}
              {tab === "games" && <GamesSection />}
              {tab === "contests" && <ComingSoon title="Contests" testId="contests-coming-soon" />}
              {tab === "events" && <ComingSoon title="Events" testId="events-coming-soon" />}
              {tab === "services" && <MyServices requests={myRequests} onReload={loadMine} />}
              {tab === "projects" && <MyProjects projects={myProjects} />}
            </>
          )}
        </section>
          </>
        )}
      </main>
    </div>
  );
}

/* ---------- gate: server-verified session ---------- */
export default function TribePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { slug } = useParams();
  const [user, setUser] = useState(() => {
    try { const cached = sessionStorage.getItem("tribe_user"); return cached ? JSON.parse(cached) : (location.state?.user || null); }
    catch { return location.state?.user || null; }
  });
  const [status, setStatus] = useState(user ? "in" : "checking");

  const handleAuthed = (u) => {
    setUser(u);
    setStatus("in");
    try { sessionStorage.setItem("tribe_user", JSON.stringify(u)); } catch {}
    const dest = sessionStorage.getItem("post_auth_dest");
    if (dest && dest !== "/tribe") {
      sessionStorage.removeItem("post_auth_dest");
      navigate(dest);
    }
  };

  useEffect(() => {
    if (user) { setStatus("in"); return; }
    fetch(`${API}/api/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((u) => { setUser(u); setStatus("in"); try { sessionStorage.setItem("tribe_user", JSON.stringify(u)); } catch {} })
      .catch(() => { setStatus("out"); sessionStorage.removeItem("tribe_user"); });
  }, [user]);

  const logout = async () => {
    await fetch(`${API}/api/auth/logout`, { method: "POST", credentials: "include" });
    setUser(null);
    setStatus("out");
    sessionStorage.removeItem("tribe_user");
  };

  if (status === "checking")
    return (
      <div className="min-h-screen" style={{ background: "#EDE7D9" }}>
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-10">
          <div className="flex justify-end mb-6">
            <div className="flex gap-3">{[1,2].map(i => <div key={i} className="w-9 h-9 rounded-full bg-[#26201612] animate-pulse" />)}</div>
          </div>
          <div className="h-10 w-48 bg-[#26201612] rounded-xl mb-3 animate-pulse" />
          <div className="h-5 w-96 bg-[#26201610] rounded-full mb-10 animate-pulse" />
          <div className="flex gap-3 mb-10">
            {[1,2,3,4,5,6].map(i => <div key={i} className="h-10 w-20 bg-[#26201610] rounded-full animate-pulse" />)}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3,4,5,6].map(i => <div key={i} className="h-72 bg-white/40 rounded-2xl animate-pulse" />)}
          </div>
        </div>
      </div>
    );
  if (status === "out")
    return (
      <LoginGate
        onAuthed={handleAuthed}
        message={slug
          ? "This post was shared from the Tribe — a private community of designers, builders and the AI-curious. Join to read it."
          : undefined}
      />
    );
  return <TribeHome user={user} onLogout={logout} post={slug || null} />;
}
